import { type SyntheticEvent, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import CMSLink from '@tmh-website/components/shared/CMSLink';
import { PageContext, useIsMobile } from '@tmh-website/shared';
import { NavigationElement, isNavigationPageLinkGroup, isValidLinkField } from '@tmh-website/shared/cms';
import { Color } from '@tmh-website/shared/colors';

import type { LocalePrefix } from '@tmh-website/shared/const';
import { determineLinkState } from '@tmh-website/services/navigation-state.service';

import * as Styled from './CategoryLink.styles';

interface CategoryLinkProps {
  element: NavigationElement;
  isOpen: boolean;
  isStandaloneMode: boolean;
  locale: LocalePrefix;
  onClick?: () => void;
  onStateChange: (state: boolean) => void;
  navigation: NavigationElement[];
}

const CategoryLink = ({
  element,
  isOpen,
  onStateChange,
  onClick,
  isStandaloneMode,
  locale,
  navigation,
}: CategoryLinkProps) => {
  const page = useContext(PageContext);
  const isMobile = useIsMobile();
  const { t } = useTranslation();

  const hasChildren = isNavigationPageLinkGroup(element);
  const link = hasChildren ? element.link.page : element.page;

  if (!page || !isValidLinkField(link)) {
    return null;
  }

  const isSelected = determineLinkState(page.path, link.value, navigation, locale, 'category-link');

  const component = (
    <Styled.Container
      as={!hasChildren && isValidLinkField(link) ? 'a' : 'div'}
      $isOpen={isOpen}
      $isSelected={isSelected}
      tabIndex={0}
      onFocus={() => {
        // Focus is used for keyboard navigation
        if (isMobile) {
          return;
        }
        onStateChange(hasChildren);
      }}
      onMouseEnter={() => {
        if (isMobile) {
          return;
        }
        onStateChange(hasChildren);
      }}
      onClick={onClick}>
      <Styled.Label>{link?.label ?? ''}</Styled.Label>
      {hasChildren && (
        <Styled.IconWrapper
          $isOpen={isOpen}
          themeColor={Color.Blue_900}
          isTransparent
          tabIndex={-1}
          onClick={(e: SyntheticEvent) => {
            if (isMobile === false) {
              return;
            }
            e.preventDefault();
            e.stopPropagation();
            onStateChange(!isOpen);
          }}
          aria-label={t('global:menu.showSubpagesOf', { label: link?.label ?? '' }) as string}>
          <Styled.ChevronIcon weight='bold' />
        </Styled.IconWrapper>
      )}
    </Styled.Container>
  );

  if (!hasChildren && isValidLinkField(link)) {
    return (
      <CMSLink key={link.value} link={link} isStandaloneMode={isStandaloneMode} locale={locale}>
        {component}
      </CMSLink>
    );
  } else return component;
};

export default CategoryLink;
